import { ComponentProps, FC } from "react";
import Link from "next/link";
import { ContentSourceImg } from "./components/ContentSourceImg";
import { ContentTags } from "./components/ContentTags";
import { OriginalBadge } from "./components/OriginalBadge";
import { RecommendOrderBadge } from "./components/RecommendOrderBadge";
import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { BookmarkButton } from "components/buttons/BookmarkButton";
import { slidein } from "styles/animation/slidein";

type Interaction = {
  "data-bookmarked"?: Mix_ContentFragment["bookmarked"];
  "data-focused": boolean;
};

const StyledBookmarkButton = styled(BookmarkButton)``;

const Container = styled(Link)<Interaction & { imgurl: string }>`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background-position: center;
  background-size: cover;

  ${({ "data-focused": focused, "data-bookmarked": bookmarked, imgurl }) => css`
    background-image: ${focused
      ? `linear-gradient(-88.63deg, rgba(0, 0, 0, 0) 2.34%, rgba(0, 0, 0, 0.9) 100%), url(${imgurl})`
      : `url(${imgurl})`};

    @media (min-width: 1025px) {
      ${StyledBookmarkButton} {
        display: ${bookmarked && focused ? "flex" : "none"};
      }

      &:hover {
        ${StyledBookmarkButton} {
          display: flex;
        }
      }
    }

    @media (max-width: 1024px) {
      background-image: ${`linear-gradient(0deg, #000000 0%, rgba(0, 0, 0, 0.2) 100%), url(${imgurl})`};
    }
  `}
`;

const Info = styled.div<Interaction>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 54px 52px 60px;

  @media (min-width: 1025px) {
    display: ${({ "data-focused": focused }) => (focused ? "flex" : "none")};
    opacity: 0;
    animation: ${slidein} 400ms ease 600ms;
    animation-fill-mode: forwards;
  }

  @media (max-width: 1024px) {
    padding: 40px 52px 44px;
  }

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  max-width: 520px;
  margin: 20px 0 36px;

  @media (max-width: 1024px) {
    margin: 20px 0 40px;
  }

  @media (max-width: 768px) {
    max-width: 80%;
    margin: 11px 0 20px;
    row-gap: 8px;
  }
`;

const Title = styled.div`
  font-size: 32px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.color.grey_f};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: keep-all;

  @media (max-width: 768px) {
    font-size: 22px;
    line-height: 1.5;
    -webkit-line-clamp: 3;
  }
`;

const Description = styled.div`
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.color.grey_c};
  line-height: 1.35;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-all;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

const ContentAuthorName = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  color: ${({ theme }) => theme.color.grey_a};
  word-break: break-all;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const RecommendContentCard: FC<
  Omit<ComponentProps<typeof Link>, "href"> & {
    content: Mix_ContentFragment;
    focused: boolean;
  }
> = ({ content, focused, ...props }) => {
  const theme = useTheme();

  return (
    <Container
      href={`/contents/${content.id}`}
      data-bookmarked={content.bookmarked}
      data-focused={focused}
      imgurl={content.imgurl ?? ""}
      target={content.isoriginal ? "_self" : "_blank"}
      rel={content.isoriginal ? "" : "noreferrer"}
      {...props}
    >
      {typeof content.recommendorder === "number" && (
        <RecommendOrderBadge
          css={css`
            display: none;

            @media (max-width: 1024px) {
              display: block;
              position: absolute;
              top: 27px;
              z-index: 1;
            }

            @media (max-width: 768px) {
              display: none;
            }
          `}
        >
          {content.recommendorder + 1}
        </RecommendOrderBadge>
      )}
      <Info data-focused={focused}>
        <OriginalBadge
          category={content.category}
          css={css`
            visibility: ${content.category && content.isoriginal
              ? "visible"
              : "hidden"};

            @media (min-width: 1025px) {
              visibility: ${content.category && content.isoriginal && focused
                ? "visible"
                : "hidden"};
            }
          `}
        />
        <Column>
          {content.mix_content_tags.length > 0 && (
            <ContentTags
              tags={content.mix_content_tags}
              css={css`
                color: ${theme.color.primary};

                @media (max-width: 768px) {
                  display: none;
                }
              `}
            />
          )}
          <Header>
            <Title>{content.title}</Title>
            {content.abstractcontents && (
              <Description>{content.abstractcontents}</Description>
            )}
          </Header>
          <Footer>
            <Row>
              {content.mix_source_info.imgurl && (
                <ContentSourceImg
                  imgurl={content.mix_source_info.imgurl}
                  css={css`
                    @media (max-width: 768px) {
                      width: 28px;
                      height: 28px;
                    }
                  `}
                />
              )}
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  row-gap: 3px;
                `}
              >
                <ContentAuthorName>{content.authorname}</ContentAuthorName>
                <ContentTags
                  tags={content.mix_content_tags}
                  css={css`
                    display: none;

                    @media (max-width: 768px) {
                      display: flex;
                      color: ${theme.color.primary};
                    }
                  `}
                />
              </div>
            </Row>
            <StyledBookmarkButton content={content} />
          </Footer>
        </Column>
      </Info>
    </Container>
  );
};
